import React, { useMemo } from "react";

import { useController, useForm } from "react-hook-form";

import {
  useDeleteOrganizationLogo,
  useUpdateOrganizationLogo,
  useUserOrganizationLogo,
} from "src/api/queries";
import { Label } from "src/base-components/Label";
import { Button } from "src/design-system/Button";
import { ErrorHint } from "src/design-system/ErrorHint";
import { Modal } from "src/design-system/Modal";
import { UploadIcon } from "src/layout/OrgLogoModal/UploadIcon";
import { DashboardPageParams } from "src/router/urls";
import { logger } from "src/utils/logger";
import { useParamsDecode } from "src/utils/useParamsDecode";

type OrgLogoModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const OrgLogoModal: React.FC<OrgLogoModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { orgId } = useParamsDecode<DashboardPageParams>();
  const {
    handleSubmit,
    reset,
    control,
    formState: { isSubmitting, errors, isValid },
  } = useForm<{ file: File | undefined | "deleted" }>({
    defaultValues: { file: undefined },
    mode: "onChange",
  });
  const { data: logoData } = useUserOrganizationLogo(orgId);

  // useController instead of <Controller>: render-prop Controllers can stop
  // delivering field updates after reset() once this component is compiled
  // (react-hook-form#12157).
  const { field: fileField } = useController({
    control,
    name: "file",
    rules: {
      validate: {
        fileSize: (file) => {
          if (file === "deleted" || file === undefined) return true;
          return file.size <= 100 * 1024 || "File must be smaller than 100 KB";
        },
        fileType: (file) => {
          if (file === "deleted" || file === undefined) return true;
          const validTypes = ["image/svg+xml", "image/png", "image/jpeg"];
          return validTypes.includes(file.type) || "Invalid file format";
        },
      },
    },
  });

  // keep-memo(react-compiler): URL.createObjectURL allocates a blob URL; the
  // explicit useMemo keeps the allocation keyed on logoData even if the
  // compiler bails on this component
  const imgSrc = useMemo(() => {
    try {
      // @ts-expect-error - 'OrganizationLogoResponse' is actually of type 'Blob | MediaSource'.
      return logoData ? URL.createObjectURL(logoData) : undefined;
    } catch (error) {
      logger.error("Error creating object URL", { error });
      return undefined;
    }
  }, [logoData]);

  const deleteLogo = useDeleteOrganizationLogo();
  const uploadLogo = useUpdateOrganizationLogo();

  const formSubmit = handleSubmit(
    async (data: { file: File | undefined | "deleted" }) => {
      if (data.file && data.file !== "deleted") {
        await uploadLogo.mutateAsync({
          organizationId: orgId,
          logo: data.file,
        });
      } else if (data.file === "deleted") {
        await deleteLogo.mutateAsync({
          organizationId: orgId,
        });
      }

      onClose();
    },
  );

  return (
    <Modal afterLeave={() => reset()} open={isOpen} onClose={onClose}>
      <Modal.Header>Edit organization</Modal.Header>
      <form onSubmit={formSubmit}>
        <Modal.Content>
          <Label mb="mb-1">Organization logo</Label>
          <div className="w-fit">
            <UploadIcon
              currentImageSource={imgSrc}
              onFileRemoved={() => {
                fileField.onChange("deleted");
              }}
              onFileSelected={(file) => {
                fileField.onChange(file);
              }}
            />
          </div>
          {errors.file?.message && (
            <ErrorHint>{errors.file?.message}</ErrorHint>
          )}
        </Modal.Content>
        <Modal.Footer
          primaryButton={
            <Button
              isDisabled={isSubmitting || !isValid}
              htmlType="submit"
              isLoading={isSubmitting}
              variant="primary"
            >
              Save
            </Button>
          }
        />
      </form>
    </Modal>
  );
};
